// Who is signed in, and which modules are switched on. Everything inside the app shell can rely on both.
import { createContext, useContext, useSyncExternalStore, type ReactNode } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { api, post, signedOut } from "./api";
import { Native } from "./native";
import type { Me, Meta, Role } from "./types";

interface Session {
  me: Me;
  meta: Meta | null;
  /** Progressive rollout: a module that is off is hidden for the team; admins see everything. */
  modOn: (k: string | null | undefined) => boolean;
  home: string;
}
const Ctx = createContext<Session | null>(null);

export const metaQuery = { queryKey: ["/api/meta"], queryFn: () => api<Meta>("/api/meta").catch(() => null) };

export function SessionProvider({ me, children }: { me: Me; children: ReactNode }) {
  // an admin may switch a module on or off: every screen picks it up within a minute
  const meta = useSuspenseQuery({ ...metaQuery, refetchInterval: 60_000 }).data;
  const modOn = (k: string | null | undefined) => !k || me.role === "admin" || !meta?.modules || meta.modules[k] !== false;
  return <Ctx.Provider value={{ me, meta, modOn, home: homeFor(me.role, modOn) }}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error("useSession outside the signed-in app");
  return s;
}

export function homeFor(role: Role, modOn: (k: string) => boolean) {
  if (role === "ops") return modOn("quotes") ? "/ops" : "/accounts";
  if (role === "admin") return "/team";
  if (role === "manager") return modOn("quotes") ? "/approvals" : "/team";
  if (role === "caller") return modOn("leads") ? "/dial" : "/calls";
  return modOn("today") ? "/today" : "/accounts";
}

export const isTeamLead = (role: Role) => role === "manager" || role === "admin";

export async function logout() {
  await post("/api/auth/logout").catch(() => {});
  Native?.logout();
  // not queryClient.clear(): that orphans the app's "me" query, so the screen stayed signed in until a reload
  signedOut();
}

const phoneQuery = matchMedia("(max-width: 820px)");
/** Phone layout (same breakpoint as the CSS). */
export const useIsMobile = () => useSyncExternalStore(
  (cb) => { phoneQuery.addEventListener("change", cb); return () => phoneQuery.removeEventListener("change", cb); },
  () => phoneQuery.matches,
);
